/**
 * Brand API client with httpOnly cookie support
 * 
 * This module provides authenticated API calls to brand-related edge functions.
 * Authentication is handled via httpOnly cookies set by the brand-auth function.
 * 
 * Dev: Uses Vite proxy (same-origin, cookies work naturally)
 * Prod: Uses direct Supabase URL (cross-origin, needs credentials: 'include')
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
const isDevMode = import.meta.env.DEV;

// Dev: Use relative URL (Vite proxy makes it same-origin)
// Prod: Use full Supabase URL (cross-origin)
const FUNCTIONS_BASE_URL = isDevMode ? '' : SUPABASE_URL;

export interface BrandApiResponse<T = unknown> {
  data?: T;
  error?: string;
}

/**
 * Call a brand edge function with httpOnly cookie credentials
 */
export async function callBrandFunction<T = unknown>(
  functionName: string,
  body: Record<string, unknown>
): Promise<BrandApiResponse<T>> {
  try {
    const response = await fetch(`${FUNCTIONS_BASE_URL}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_PUBLISHABLE_KEY,
      },
      // Dev: same-origin (via proxy), Prod: include (cross-origin cookies)
      credentials: isDevMode ? 'same-origin' : 'include',
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok) {
      return { error: data.error || `Request failed with status ${response.status}` };
    }

    return { data: data as T };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Network error' };
  }
}

/**
 * Call brand-auth function
 */
export async function callBrandAuth<T = unknown>(
  action: string,
  body: Record<string, unknown> = {}
): Promise<BrandApiResponse<T>> {
  return callBrandFunction<T>('brand-auth', { action, ...body });
}

/**
 * Call brand-campaigns function
 */
export async function callBrandCampaigns<T = unknown>(
  action: string,
  data: Record<string, unknown> = {}
): Promise<BrandApiResponse<T>> {
  return callBrandFunction<T>('brand-campaigns', { action, data });
}

/**
 * Call brand-api-keys function
 */
export async function callBrandApiKeys<T = unknown>(
  action: string,
  params: Record<string, unknown> = {}
): Promise<BrandApiResponse<T>> {
  return callBrandFunction<T>('brand-api-keys', { action, ...params });
}
